import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceTypes = [
  { value: "standard", label: "Standard Cleaning" },
  { value: "deep", label: "Deep Cleaning" },
  { value: "move", label: "Move-In/Out Cleaning" },
  { value: "apartment", label: "Apartment Cleaning" },
  { value: "office", label: "Office Cleaning" },
  { value: "recurring", label: "Recurring Cleaning" },
];

const QuoteForm = ({ variant = "default" }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    zip_code: "",
    service_type: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/quotes`, formData);
      toast.success("Quote request submitted!", {
        description: "We'll contact you within 24 hours.",
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        zip_code: "",
        service_type: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Failed to submit", {
        description: "Please try again or call us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isCompact = variant === "compact";

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-4 ${isCompact ? "" : "space-y-6"}`}
      data-testid="quote-form"
    >
      <div className={`grid gap-4 ${isCompact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-medium text-slate-700">
            Full Name *
          </Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="John Smith"
            required
            className="h-12 bg-slate-50 border-slate-200 focus:border-primary rounded-xl"
            data-testid="quote-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-slate-700">
            Email Address *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john@example.com"
            required
            className="h-12 bg-slate-50 border-slate-200 focus:border-primary rounded-xl"
            data-testid="quote-email-input"
          />
        </div>
      </div>

      <div className={`grid gap-4 ${isCompact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
            Phone Number *
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(555) 123-4567"
            required
            className="h-12 bg-slate-50 border-slate-200 focus:border-primary rounded-xl"
            data-testid="quote-phone-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="zip_code" className="text-sm font-medium text-slate-700">
            ZIP Code *
          </Label>
          <Input
            id="zip_code"
            name="zip_code"
            value={formData.zip_code}
            onChange={handleChange}
            placeholder="30303"
            required
            className="h-12 bg-slate-50 border-slate-200 focus:border-primary rounded-xl"
            data-testid="quote-zip-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="service_type" className="text-sm font-medium text-slate-700">
          Service Type *
        </Label>
        <Select value={formData.service_type} onValueChange={handleServiceChange} required>
          <SelectTrigger
            className="h-12 bg-slate-50 border-slate-200 focus:border-primary rounded-xl"
            data-testid="quote-service-select"
          >
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            {serviceTypes.map((service) => (
              <SelectItem key={service.value} value={service.value}>
                {service.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!isCompact && (
        <div className="space-y-2">
          <Label htmlFor="message" className="text-sm font-medium text-slate-700">
            Additional Details
          </Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your space and any special requirements..."
            rows={4}
            className="bg-slate-50 border-slate-200 focus:border-primary rounded-xl resize-none"
            data-testid="quote-message-input"
          />
        </div>
      )}

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full h-12 bg-accent hover:bg-accent/90 text-white font-bold rounded-full btn-accent-hover"
        data-testid="quote-submit-btn"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="w-5 h-5 mr-2" />
            Get Free Quote
          </>
        )}
      </Button>
    </form>
  );
};

export default QuoteForm;
